import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, ArrowRightIcon, MapPinIcon, CalendarIcon, MountainSnowIcon, BuildingIcon, FileTextIcon, XIcon, TrendingUpIcon } from 'lucide-react';
interface SearchPageProps {
  onNavigate: (page: string) => void;
}
type Category = 'all' | 'tours' | 'hotels' | 'pages';
interface SearchItem {
  id: string;
  title: string;
  category: 'tours' | 'hotels' | 'pages';
  description: string;
  meta: string;
  image?: string;
  page?: string;
}
const searchData: SearchItem[] = [{
  id: 'ebc',
  title: 'Everest Base Camp Luxury Trek',
  category: 'tours',
  description: "Trek to the foot of the world's highest peak with luxury lodges and personal Sherpa guides.",
  meta: '14 Days · From $4,500',
  image: 'https://images.unsplash.com/photo-1516302752625-fcc3c50ae61f?w=300&q=80',
  page: 'everest-trek'
}, {
  id: 'annapurna',
  title: 'Annapurna Circuit Premium',
  category: 'tours',
  description: 'Circle the Annapurna massif with premium teahouse stays and helicopter transfers.',
  meta: '12 Days · From $3,800',
  image: 'https://images.unsplash.com/photo-1585409677983-0f6c41ca9c3b?w=300&q=80'
}, {
  id: 'chitwan',
  title: 'Chitwan Luxury Safari',
  category: 'tours',
  description: 'Encounter rhinos, tigers, and exotic birds from a five-star jungle lodge.',
  meta: '5 Days · From $2,200',
  image: 'https://images.unsplash.com/photo-1574950578143-858c6fc139d8?w=300&q=80'
}, {
  id: 'mustang',
  title: 'Upper Mustang Expedition',
  category: 'tours',
  description: 'Explore the forbidden kingdom of Lo Manthang with luxury camping.',
  meta: '10 Days · From $5,200',
  image: 'https://images.unsplash.com/photo-1503220317375-aaad61436b1b?w=300&q=80'
}, {
  id: 'langtang',
  title: 'Langtang Valley Trek',
  category: 'tours',
  description: 'Discover the "Valley of Glaciers" with pristine forests and Tamang culture.',
  meta: '8 Days · From $2,800',
  image: 'https://images.unsplash.com/photo-1486911278844-a81c5267e227?w=300&q=80'
}, {
  id: 'kathmandu',
  title: 'Kathmandu Heritage Tour',
  category: 'tours',
  description: 'Explore UNESCO World Heritage sites with private guides and luxury stays.',
  meta: '4 Days · From $1,500',
  image: 'https://images.unsplash.com/photo-1558799401-1dcba79834c2?w=300&q=80'
}, {
  id: 'dwarika',
  title: "Dwarika's Hotel",
  category: 'hotels',
  description: 'A living museum of Nepali art and architecture in the heart of Kathmandu.',
  meta: 'Kathmandu · $350/night',
  image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=300&q=80',
  page: 'dwarika-hotel'
}, {
  id: 'everest-view',
  title: 'Everest View Hotel',
  category: 'hotels',
  description: "The world's highest luxury hotel with panoramic Everest views.",
  meta: 'Namche Bazaar · $280/night',
  image: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=300&q=80',
  page: 'hotels-everest'
}, {
  id: 'pavilions',
  title: 'Pavilions Himalayas',
  category: 'hotels',
  description: 'Eco-luxury resort with private villas and unobstructed Annapurna views.',
  meta: 'Pokhara · $420/night',
  image: 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=300&q=80',
  page: 'hotels-pokhara'
}, {
  id: 'club-himalaya',
  title: 'Club Himalaya',
  category: 'hotels',
  description: 'Premier hilltop resort with 360-degree mountain views and heated pool.',
  meta: 'Nagarkot · $200/night',
  image: 'https://images.unsplash.com/photo-1596394516093-501ba68a0ba6?w=300&q=80',
  page: 'hotels-nagarkot'
}, {
  id: 'about',
  title: 'About Us',
  category: 'pages',
  description: 'Our story, mission, team, and what makes Nepal Luxury Travel different.',
  meta: 'Company',
  page: 'about'
}, {
  id: 'contact',
  title: 'Contact Us',
  category: 'pages',
  description: 'Get in touch via WhatsApp, phone, email, or our contact form.',
  meta: 'Support',
  page: 'contact'
}, {
  id: 'plan-trip',
  title: 'Plan a Trip',
  category: 'pages',
  description: 'Submit your travel preferences and get a personalized itinerary.',
  meta: 'Booking',
  page: 'plan-trip'
}, {
  id: 'hotels-everest-page',
  title: 'Hotels in Everest Region',
  category: 'pages',
  description: 'Luxury stays at the top of the world near Everest Base Camp.',
  meta: 'Destination',
  page: 'hotels-everest'
}, {
  id: 'hotels-pokhara-page',
  title: 'Hotels in Pokhara',
  category: 'pages',
  description: 'Lakeside luxury with stunning Annapurna mountain views.',
  meta: 'Destination',
  page: 'hotels-pokhara'
}, {
  id: 'hotels-nagarkot-page',
  title: 'Hotels in Nagarkot',
  category: 'pages',
  description: 'Sunrise views over the entire Himalayan range.',
  meta: 'Destination',
  page: 'hotels-nagarkot'
}];
const categoryIcons: Record<string, React.ReactNode> = {
  tours: <MountainSnowIcon className="w-3.5 h-3.5" data-id="element-1997" />,
  hotels: <BuildingIcon className="w-3.5 h-3.5" data-id="element-1998" />,
  pages: <FileTextIcon className="w-3.5 h-3.5" data-id="element-1999" />
};
const categoryColors: Record<string, string> = {
  tours: 'bg-teal-50 text-teal-700 border-teal-100',
  hotels: 'bg-amber-50 text-amber-700 border-amber-100',
  pages: 'bg-slate-50 text-slate-600 border-slate-200'
};
export function SearchPage({
  onNavigate
}: SearchPageProps) {
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<Category>('all');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onNavigate('home');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onNavigate]);
  const filtered = searchData.filter(item => {
    const matchesCategory = activeCategory === 'all' || item.category === activeCategory;
    const matchesQuery = !query.trim() || item.title.toLowerCase().includes(query.toLowerCase()) || item.description.toLowerCase().includes(query.toLowerCase());
    return matchesCategory && matchesQuery;
  });
  const hasQuery = query.trim().length > 0;
  const categories: {
    key: Category;
    label: string;
  }[] = [{
    key: 'all',
    label: 'All'
  }, {
    key: 'tours',
    label: 'Tours'
  }, {
    key: 'hotels',
    label: 'Hotels'
  }, {
    key: 'pages',
    label: 'Pages'
  }];
  const popularSearches = [{
    label: 'Everest Base Camp',
    query: 'Everest'
  }, {
    label: 'Luxury Hotels',
    query: 'Hotel'
  }, {
    label: 'Pokhara',
    query: 'Pokhara'
  }, {
    label: 'Safari',
    query: 'Safari'
  }, {
    label: 'Annapurna',
    query: 'Annapurna'
  }];
  return <div className="w-full min-h-screen bg-white" data-id="element-2000">
      {/* Search Header */}
      <section className="w-full pt-28 sm:pt-32 pb-6 sm:pb-8 border-b border-slate-100" data-id="element-2001">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2002">
          <div className="flex items-center gap-4" data-id="element-2003">
            <SearchIcon className="w-6 h-6 sm:w-7 sm:h-7 text-slate-300 flex-shrink-0" data-id="element-2004" />
            <input ref={inputRef} type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search tours, hotels, destinations..." className="flex-1 text-2xl sm:text-3xl font-playfair text-slate-900 placeholder:text-slate-300 focus:outline-none bg-transparent" data-id="element-2005" />
            {query && <button onClick={() => setQuery('')} className="p-1.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors" data-id="element-2006">
                <XIcon className="w-5 h-5" data-id="element-2007" />
              </button>}
          </div>

          {/* Category Filters */}
          <div className="flex items-center gap-2 mt-6" data-id="element-2008">
            {categories.map(cat => <button key={cat.key} onClick={() => setActiveCategory(cat.key)} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${activeCategory === cat.key ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`} data-id="element-2009">
                {cat.label}
              </button>)}
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="w-full py-8 sm:py-12" data-id="element-2010">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2011">
          {/* Popular Searches (when no query) */}
          {!hasQuery && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4
        }} className="mb-10" data-id="element-2012">
              <div className="flex items-center gap-2 mb-4" data-id="element-2013">
                <TrendingUpIcon className="w-4 h-4 text-slate-400" data-id="element-2014" />
                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider" data-id="element-2015">
                  Popular Searches
                </h3>
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-2016">
                {popularSearches.map(item => <button key={item.label} onClick={() => setQuery(item.query)} className="px-4 py-2 bg-[#FAF8F5] border border-[#D4A853]/10 rounded-full text-sm text-slate-700 hover:border-teal-300 hover:bg-teal-50 transition-all duration-200" data-id="element-2017">
                    {item.label}
                  </button>)}
              </div>
            </motion.div>}

          {/* Results Count */}
          {hasQuery && <p className="text-sm text-slate-400 mb-6" data-id="element-2018">
              {filtered.length} result{filtered.length !== 1 ? 's' : ''} for "
              {query}"
            </p>}

          {/* Result Cards */}
          <div className="space-y-3" data-id="element-2019">
            <AnimatePresence mode="popLayout" data-id="element-2020">
              {filtered.map((item, i) => <motion.button key={item.id} layout initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.3,
              delay: i * 0.03
            }} onClick={() => item.page && onNavigate(item.page)} disabled={!item.page} className={`w-full flex items-center gap-4 sm:gap-5 p-4 rounded-xl border border-slate-100 text-left transition-all duration-200 ${item.page ? 'hover:border-teal-200 hover:shadow-md hover:shadow-teal-600/5 cursor-pointer' : 'opacity-70'}`} data-id="element-2021">
                  {/* Thumbnail */}
                  {item.image ? <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden flex-shrink-0" data-id="element-2022">
                      <img src={item.image} alt={item.title} className="w-full h-full object-cover" data-id="element-2023" />
                    </div> : <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg bg-[#FAF8F5] flex items-center justify-center flex-shrink-0" data-id="element-2024">
                      {categoryIcons[item.category]}
                    </div>}

                  {/* Content */}
                  <div className="flex-1 min-w-0" data-id="element-2025">
                    <div className="flex items-center gap-2 mb-1" data-id="element-2026">
                      <h3 className="font-semibold text-slate-900 text-sm sm:text-base truncate" data-id="element-2027">
                        {item.title}
                      </h3>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border flex-shrink-0 ${categoryColors[item.category]}`} data-id="element-2028">
                        {categoryIcons[item.category]}
                        {item.category}
                      </span>
                    </div>
                    <p className="text-slate-500 text-xs sm:text-sm line-clamp-1" data-id="element-2029">
                      {item.description}
                    </p>
                    <p className="text-slate-400 text-xs mt-1" data-id="element-2030">{item.meta}</p>
                  </div>

                  {/* Arrow */}
                  {item.page && <ArrowRightIcon className="w-4 h-4 text-slate-300 flex-shrink-0" data-id="element-2031" />}
                </motion.button>)}
            </AnimatePresence>

            {filtered.length === 0 && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="text-center py-16" data-id="element-2032">
                <SearchIcon className="w-10 h-10 text-slate-200 mx-auto mb-4" data-id="element-2033" />
                <p className="text-slate-400 text-lg font-playfair" data-id="element-2034">
                  No results found
                </p>
                <p className="text-slate-400 text-sm mt-1" data-id="element-2035">
                  Try a different search term
                </p>
              </motion.div>}
          </div>

          {/* ESC hint */}
          <div className="mt-12 text-center" data-id="element-2036">
            <p className="text-xs text-slate-300" data-id="element-2037">
              Press{' '}
              <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-slate-400 text-[10px] font-mono" data-id="element-2038">
                ESC
              </kbd>{' '}
              to go back
            </p>
          </div>
        </div>
      </section>
    </div>;
}